import {
  DatePipe,
  NgClass,
  NgOptimizedImage
} from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { NgxTolgeeModule } from '@tolgee/ngx';
import {
  clipAquaoText,
  sanitizeAquaoText
} from '../../../../../../core/helpers/aquao-text.helper';
import { isMinor } from '../../../../../../core/helpers/age.helper';
import { AQUAO_TEXT_UX_MAX } from '../../../../../../core/model/aquao-field-limits.model';
import { AvailabilitySpaBooking } from '../../../../../../core/model/availability-spa-booking.model';
import { SpaBookingSlot } from '../../../../../../core/model/spa-booking-slot.model';
import { AvailableSpaBooking } from '../../../../../../core/model/spa-booking.model';
import { SignInComponent } from '../../../account/sign-in/sign-in.component';
import { emulateClick } from '../../../core/a11y';
import { DrawerService } from '../../../drawer/drawer.service';
import { NotificationService } from '../../../notification/notification.service';
import {
  Custom,
  DatePickerComponent
} from '../../date-picker/date-picker.component';
import { AutoCloseDirective } from '../../directives/auto-close.directive';
import { HoverGradientDirective } from '../../directives/hover-gradient.directive';
import { ProgressClickDirective } from '../../directives/progress-click.directive';
import { Center } from '../../model/center.model';
import { AccountService } from '../../services/account.service';
import { AuthService } from '../../services/auth.service';
import { CharacterCountComponent } from '../character-count/character-count.component';

@Component({
  selector: 'app-spa-booking',
  templateUrl: 'spa-booking.component.html',
  imports: [
    HoverGradientDirective,
    DatePickerComponent,
    AutoCloseDirective,
    NgOptimizedImage,
    NgxTolgeeModule,
    NgClass,
    ReactiveFormsModule,
    DatePipe,
    ProgressClickDirective,
    CharacterCountComponent
  ]
})
export class SpaBookingComponent {
  readonly AQUAO_TEXT_UX_MAX = AQUAO_TEXT_UX_MAX;
  accountService = inject(AccountService);
  authService = inject(AuthService);
  drawerService = inject(DrawerService);
  notificationService = inject(NotificationService);
  center = input.required<Center>();
  spaBooking = input.required<AvailableSpaBooking>();
  /** Commentaires déjà persisté pour ce créneau, repris à la réouverture du planning. */
  initialCommentaires = input<string>('');
  /** Cabine duo : choisie avant l'ouverture du planning, elle ne change pas pendant la planification. */
  duo = input(false);
  validated = output<SpaBookingSlot>();
  /** Suffixe d'identifiants, plusieurs plannings pouvant coexister sur une même page. */
  id = Math.random().toString(36).substring(2);
  commentaireControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [ Validators.maxLength(AQUAO_TEXT_UX_MAX) ]
  });
  selectedSpaItem = signal<{
    label: string,
    erpId: number,
    duration: number
  } | null>(null);
  availableDays = signal<Custom<Date>[]>([]);
  selectedDay = signal<AvailabilitySpaBooking | null>(null);
  availableSlots = computed(() => {
    const selectedSpaItem = this.selectedSpaItem();
    const selectedDay = this.selectedDay();
    if (!selectedSpaItem || !selectedDay) {
      return null;
    }
    const slots: Date[] = []
    for (const slot of selectedDay.slots) {
      const [ hour, minute ] = slot.from.split(':').map(Number);
      const [ maxHour, maxMinute ] = slot.to.split(':').map(Number);
      const start = Date.UTC(1970, 0, 1, hour, Math.ceil(minute / 15) * 15, 0)
      const max = new Date(Date.UTC(1970, 0, 1, maxHour, maxMinute + 1, 0));
      const time = new Date(start);
      while (time.getTime() + selectedSpaItem.duration * 60 * 1000 <= max.getTime()) {
        slots.push(new Date(time));
        time.setUTCMinutes(time.getUTCMinutes() + 15);
      }
    }
    slots.sort((a, b) => a.getTime() - b.getTime());
    return slots.map(date => {
      const hour = date.getUTCHours().toLocaleString(undefined, { minimumIntegerDigits: 2 });
      const minute = date.getUTCMinutes().toLocaleString(undefined, { minimumIntegerDigits: 2 });
      return `${hour}:${minute}`;
    })
  });
  selectedSlot = signal<string | null>(null);
  actualSlot = signal<string | null>(null);
  selectSlotProgress = signal(false);
  updatePlanningProgress = signal(false);
  selectedPeriod = computed(() => {
    const from = new Date();
    from.setDate(from.getDate() + this.center().bookingLeadTime);
    const to = new Date(from);
    to.setMonth(to.getMonth() + 2);
    return { from, to };
  });
  spaEmail = computed(() => this.center().spaEmail || 'spa@caliceo.com');
  timeSlotsView = viewChild<ElementRef<HTMLElement>>('timeSlots');
  protected readonly emulateClick = emulateClick;
  /** Dernière valeur poussée par le parent, pour distinguer une saisie utilisateur d'une reprise. */
  private appliedCommentaires = '';

  constructor() {
    effect(() => {
      this.applyInitialCommentaires(clipAquaoText(this.initialCommentaires(), AQUAO_TEXT_UX_MAX));
    });
    this.commentaireControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(value => {
        const sanitizedValue = sanitizeAquaoText(value);
        if (sanitizedValue !== value) {
          this.commentaireControl.setValue(sanitizedValue, { emitEvent: false });
        }
      });
    effect(() => {
      const target = this.timeSlotsView()?.nativeElement;
      if (!this.availableSlots()?.length || !target) {
        return;
      }
      // Target is outside the viewport from the bottom
      if (target.getBoundingClientRect().bottom > window.innerHeight) {
        target.scrollIntoView(false);
      }
      // Target is outside the view from the top
      if (target.getBoundingClientRect().top < 0) {
        target.scrollIntoView();
      }

    });
    effect(async () => {
      const user = this.authService.user();
      const center = this.center();
      const spaBooking = this.spaBooking();
      const selectedSpaItem = this.selectedSpaItem();
      if (!selectedSpaItem) {
        this.selectedSpaItem.set(spaBooking.items[0]);
        return;
      }
      const selectedDay = this.selectedDay();
      const selectedSlot = this.selectedSlot();
      if (!user?.authCompleted || !user.erpId || !selectedSpaItem || !selectedDay || !selectedSlot) {
        this.actualSlot.set(null)
        return;
      }
      // L'accès au Spa est réservé aux personnes majeures, vérifié dès le choix du créneau
      if (isMinor(user.birthDate)) {
        this.selectedSlot.set(null);
        this.actualSlot.set(null);
        this.selectSlotProgress.set(false);
        this.notificationService.open({
          type: 'error',
          title: 'cannotPlan',
          message: 'spaMinorForbidden'
        });
        return;
      }
      try {
        const actualSlot = await this.accountService.planSpa(selectedDay.date, selectedSlot, user.erpId,
          selectedSpaItem.erpId, spaBooking.type === 'option' ? spaBooking.produitCadeauOid : spaBooking.erpId,
          spaBooking.type, center.erpId, true, undefined, this.duo());
        this.actualSlot.set(actualSlot);
      } catch {
        this.notificationService.open({
          type: 'error',
          title: 'cannotPlan',
          message: 'cannotPlanDescription'
        });
      } finally {
        this.selectSlotProgress.set(false);
      }
    });
    effect(async () => {
      const selectedSpaItem = this.selectedSpaItem();
      // Relit les disponibilités si la cabine duo change
      this.duo();
      if (!selectedSpaItem) {
        return;
      }
      await this.updatePlanning(selectedSpaItem);
    });
  }

  // La convention projet impose un @returns même sur une méthode void
  // eslint-disable-next-line jsdoc/require-returns-check
  /**
   * Émet le créneau spa validé, commentaires inclus.
   * @returns Promise<void> une fois l'émission effectuée ou si aucun créneau n'est sélectionné.
   */
  planSpa = async (): Promise<void> => {
    const selectedSpaItem = this.selectedSpaItem();
    const selectedDay = this.selectedDay();
    const actualSlot = this.actualSlot();
    if (!selectedSpaItem || !selectedDay || !actualSlot) {
      return;
    }
    this.validated.emit({
      date: selectedDay.date,
      time: actualSlot,
      productId: selectedSpaItem.erpId,
      created: new Date().toISOString(),
      commentaires: clipAquaoText(this.commentaireControl.value, AQUAO_TEXT_UX_MAX),
      duo: this.duo()
    });
    this.actualSlot.set(null)
  }

  /**
   * The select new spa item
   * @param selectedSpaItem The selected SPA item
   * @param selectedSpaItem.label SPA item label
   * @param selectedSpaItem.erpId SPA item erp id
   * @param selectedSpaItem.duration SPA item duration
   */
  async selectSpaItem(selectedSpaItem: { label: string, erpId: number, duration: number }): Promise<void> {
    this.selectedSpaItem.set(selectedSpaItem)
  }

  selectDay($event: unknown): void {
    this.selectedDay.set($event as AvailabilitySpaBooking);
    this.selectedSlot.set(null);
    this.actualSlot.set(null);
  }

  /**
   * New time slot selected by the user
   * @param slot The time slot
   */
  selectSlot(slot: string): void {
    if (!this.authService.user()?.authCompleted) {
      this.drawerService.open({ component: SignInComponent, inputs: { inDrawer: true }, style: 'transparent' });
    }
    this.selectedSlot.set(null);
    this.selectSlotProgress.set(true);
    this.selectedSlot.set(slot);
    this.actualSlot.set(null);
  }

  // La convention projet impose un @returns même sur une méthode void
  // eslint-disable-next-line jsdoc/require-returns-check
  /**
   * Reprend le commentaires persisté par le parent sans écraser une saisie en cours.
   * @param commentaires Commentaires déjà sanitisé et clipé à la limite Aquao.
   * @returns Rien.
   */
  private applyInitialCommentaires(commentaires: string): void {
    // Une valeur divergente signifie que l'utilisateur a édité le champ : on le laisse tel quel.
    if (this.commentaireControl.value !== this.appliedCommentaires) {
      return;
    }
    this.appliedCommentaires = commentaires;
    if (this.commentaireControl.value !== commentaires) {
      this.commentaireControl.setValue(commentaires, { emitEvent: false });
    }
  }

  /**
   * Display planning availabilities
   * @param selectedSpaItem SPA item
   * @param selectedSpaItem.label Item label
   * @param selectedSpaItem.erpId Center id
   * @param selectedSpaItem.duration item duration
   */
  private async updatePlanning(selectedSpaItem: { label: string; erpId: number; duration: number }): Promise<void> {
    this.updatePlanningProgress.set(true);
    const availabilities = await this.accountService.availabilitySpaBooking(
      this.center().erpId,
      selectedSpaItem.erpId,
      this.selectedPeriod().from.toISOString().slice(0, 10),
      this.selectedPeriod().to.toISOString().slice(0, 10),
      this.duo()
    );
    const availableDays = availabilities.map(a => ({
      from: new Date(`${a.date}T00:00:00Z`),
      to: new Date(`${a.date}T00:00:00Z`),
      css: [ 'border', '!text-white', '!bg-bleuCaliceo-300' ],
      data: a
    } as Custom<Date>));
    this.availableDays.set(availableDays);
    this.updatePlanningProgress.set(false);
  }
}
